import { SensorDataSession } from "@shared/models";
import { convertDateToTimezone } from "@shared/utils";
import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { SensorDataSessionStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const defaultFilter = {
  page: 0,
  limit: 50,
  search: "",
};

export const initialState: SensorDataSessionStateType = {
  sessions: [],
  sessionsTotal: 0,
  filter: { ...defaultFilter },

  current_session: null,
  current_session_values: [],
  current_session_processed_values: [],
  current_session_values_filter: null,
  current_session_markers: [],
  current_session_dots: [],

  files_perscentage: 0,
};

const reducer = createReducer<SensorDataSessionStateType, Action>(initialState)
  .handleAction(actions.getSensorDataSessions.success, (state, action) =>
    produce(state, (nextState) => {
      const { rows, count, is_clear } = action.payload;
      nextState.sessions = !is_clear ? [...nextState.sessions, ...rows] : [...rows];
      nextState.sessionsTotal = count;
    }),
  )
  .handleAction(actions.setFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.filter = action.payload || { ...defaultFilter };
    }),
  )
  .handleAction(actions.clearSensorDataSessions, (state) =>
    produce(state, (nextState) => {
      nextState.sessions = [];
      nextState.sessionsTotal = 0;
    }),
  )
  .handleAction(actions.createSensorDataSession.success, (state, action) =>
    produce(state, (nextState) => {
      const session = action.payload.session;
      nextState.sessions = [{ ...session, files_count: session.files.length }, ...nextState.sessions];
    }),
  )
  .handleAction(actions.setFilesPercentage, (state, action) =>
    produce(state, (nextState) => {
      nextState.files_perscentage = action.payload;
    }),
  )
  .handleAction(actions.getSensorDataSession.success, (state, action) =>
    produce(state, (nextState) => {
      const session = action.payload;

      nextState.current_session = session
        ? ({
            ...session,
            station_start_date: convertDateToTimezone(session.station_start_date, session.station_timezone),
            station_end_date: convertDateToTimezone(session.station_end_date, session.station_timezone),
          } as SensorDataSession)
        : null;
    }),
  )
  .handleAction(actions.deleteSensorDataSession.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.sessions = nextState.sessions.filter((s) => s.id !== action.payload.session_id);
      nextState.current_session = null;
    }),
  )
  .handleAction(actions.updateSensorDataSession.success, (state, action) =>
    produce(state, (nextState) => {
      const session = action.payload.session;

      nextState.current_session = {
        ...session,
        station_start_date: convertDateToTimezone(session.station_start_date, session.station_timezone),
        station_end_date: convertDateToTimezone(session.station_end_date, session.station_timezone),
      };
    }),
  )
  .handleAction(actions.getSensorDataSessionValues.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_values = action.payload;
    }),
  )
  .handleAction(actions.getSensorDataSessionProcessedValues.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_processed_values = action.payload;
    }),
  )
  .handleAction(actions.setCurrentSessionValuesFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_values_filter = action.payload;
    }),
  )
  .handleAction(actions.createSensorDataSessionMarker.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_markers = [action.payload.marker, ...nextState.current_session_markers];
    }),
  )
  .handleAction(actions.getSensorDataSessionMarkers.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_markers = action.payload;
    }),
  )
  .handleAction(actions.getSensorDataSessionDots.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_dots = action.payload;
    }),
  )
  .handleAction(actions.deleteSensorDataSessionDot.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_dots = nextState.current_session_dots.filter((d) => d.id !== action.payload.dot_id);
    }),
  )
  .handleAction(actions.deleteSensorDataSessionMarker.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.current_session_markers = nextState.current_session_markers.filter(
        (m) => action.payload.marker_id !== m.id,
      );
      nextState.current_session_dots = nextState.current_session_dots.filter(
        (d) => d.session_marker_id !== action.payload.marker_id,
      );
    }),
  );

export { reducer as SensorDataSessionReducer };
